import TitleLayout from '@/components/Layout/TitleLayout';
import Banner from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner';
import { BannerType } from '@oncolens/oncolens-ui/lib/components/Common/Banner/Banner.helpers';
import MarkdownContainer from '@oncolens/oncolens-ui/lib/components/Common/MarkdownContainer';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import Combobox from '@oncolens/oncolens-ui/lib/components/FormControls/Combobox/Combobox';
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import MultiListbox from '@oncolens/oncolens-ui/lib/components/FormControls/MultiListbox';
import RadioButtons from '@oncolens/oncolens-ui/lib/components/FormControls/RadioButtons';
import TextAreaInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextAreaInput';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import { CreatedFormStore } from '@oncolens/oncolens-ui/lib/libraries/FormStore';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import React, { useState } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import shallow from 'zustand/shallow';
import {
  BehaviorConfigForManipulationDto,
  BehaviorConfigFormValues,
  BehaviorConfigStatus,
  createBehaviorConfig,
  updateBehaviorConfig,
  useDisplayPagesData,
} from '../api/behavior-configs';
import { useHydrateBehaviorConfig } from '../behavior-config.hooks';

export enum BehaviorConfigType {
  Alert = 'Alert',
  Thumbnail = 'Thumbnail',
  Link = 'Link',
}

const behaviorConfigTypes = [
  { label: BehaviorConfigType.Alert, value: BehaviorConfigType.Alert },
  { label: BehaviorConfigType.Thumbnail, value: BehaviorConfigType.Thumbnail },
  { label: BehaviorConfigType.Link, value: BehaviorConfigType.Link },
];

export enum BehaviorConfigIsCme {
  Yes = 'Yes',
  No = 'No',
}

const behaviorConfigIsCmeOptions = [
  { label: BehaviorConfigIsCme.Yes, value: BehaviorConfigIsCme.Yes },
  { label: BehaviorConfigIsCme.No, value: BehaviorConfigIsCme.No },
];

const getIsCmeValueToString = (value?: boolean) => {
  switch (value) {
    case true:
      return BehaviorConfigIsCme.Yes;
    case false:
      return BehaviorConfigIsCme.No;
    default:
      return '';
  }
};

const getIsCmeFromString = (value?: string) => {
  switch (value) {
    case BehaviorConfigIsCme.Yes:
      return true;
    case BehaviorConfigIsCme.No:
      return false;
    default:
      return null;
  }
};

const schema = yup.object().shape({
  assignAllOrganizations: yup.boolean(),
  organizationIds: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .when('assignAllOrganizations', {
      is: (allSelected: boolean) => !allSelected,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      then: (schema) => schema.min(1, 'At least one organization is required.').required(),
    }),
  displayPages: yup.array(yup.string()).min(1, 'At least one display page is required.').required(),
  userSpecialties: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .required(),
  type: yup.string().required('Type is required.'),
  groupRefCode: yup.string().nullable(),
  status: yup.string().required('Status is required.'),
  diseaseTypeCriteria: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .nullable(),
  diseaseLocationCriteria: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .nullable(),
  diseaseSettingCriteria: yup
    .array(yup.object().shape({ label: yup.string(), value: yup.string() }))
    .nullable(),
  message: yup.string().required('Message is required.'),
  isCme: yup.boolean().when('type', {
    is: (type: string) => type === BehaviorConfigType.Thumbnail,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    then: (schema) => schema.required('Is CME is required'),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    otherwise: (schema) => schema.nullable(),
  }),
  dateRangeFilters: yup
    .object()
    .shape({
      startDate: yup.date().nullable(),
      endDate: yup.date().nullable(),
    })
    .test('validDateRangeTest', 'Start date must be before End Date', (value: any) => {
      if (value.startDate && value.endDate) {
        return value.startDate <= value.endDate;
      }
      return true;
    })
    .test(
      'validFieldsTest',
      'Need both start date and end date or neither',
      (value: any) => !((value.startDate && !value.endDate) || (!value.startDate && value.endDate)),
    ),
});

const defaultValues = {
  organizationIds: null,
  assignAllOrganizations: false,
  displayPages: [],
  userSpecialties: [],
  type: '',
  dateRangeFilters: {},
  groupRefCode: null,
  status: BehaviorConfigStatus.Inactive,
  diseaseTypeCriteria: [],
  diseaseLocationCriteria: [],
  diseaseSettingCriteria: [],
  message: '',
  isCme: undefined,
} as BehaviorConfigFormValues;

export const ManageBehaviorConfigs = () => {
  const formStore = useFormStore(defaultValues, schema);
  const [updateField, updateStore, isValid, type, assignAllOrganizations] = formStore(
    (x) => [x.updateField, x.updateStore, x.isValid, x.getValue('type'), x.assignAllOrganizations],
    shallow,
  );
  const { configId, orgData, pathologies, locations, userSpecialties, settingData } =
    useHydrateBehaviorConfig(updateStore);
  const displayPages = useDisplayPagesData();

  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);

    const formData = store.getValues();
    const {
      organizationIds,
      diseaseLocationCriteria,
      diseaseSettingCriteria,
      diseaseTypeCriteria,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      userSpecialties,
      ...configData
    } = formData;

    const data = {
      ...configData,
      organizationIds: organizationIds?.map((x) => x.value),
      diseaseLocationCriteria: diseaseLocationCriteria?.map((x) => x.value),
      diseaseSettingCriteria: diseaseSettingCriteria?.map((x) => x.value),
      diseaseTypeCriteria: diseaseTypeCriteria?.map((x) => x.value),
      userSpecialties: userSpecialties?.map((x) => x.value),
      startDate: formData.dateRangeFilters && formData.dateRangeFilters.startDate,
      endDate: formData.dateRangeFilters && formData.dateRangeFilters.endDate,
    } as BehaviorConfigForManipulationDto;

    if (configId) {
      updateBehaviorConfig(configId, data)
        .then(() => {
          setSaving(false);
          navigate('/behavior-config');
        })
        .catch((error) => {
          setSaving(false);
          console.error(error);
        });
    } else {
      createBehaviorConfig(data)
        .then(() => {
          setSaving(false);
          navigate('/behavior-config');
        })
        .catch((error) => {
          setSaving(false);
          console.error(error);
        });
    }
  };

  return (
    <TitleLayout title={`${configId ? 'Edit' : 'Add'} Behavior Config`}>
      <div className="flex flex-col flex-grow overflow-y-auto bg-white rounded-lg shadow-lg max-w-7xl">
        <div className="grid gap-3 px-4 pt-4 overflow-y-auto flex-grow-1">
          <FormField name="organizationIds" store={formStore}>
            {({ value, onChange }) => {
              const showBanner =
                !!value && value.length === orgData.length && !assignAllOrganizations;

              return (
                <div className="col-span-2">
                  <Combobox
                    id="organizations"
                    htmlName="organizations"
                    label="Organizations"
                    data={orgData}
                    value={value}
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    onChange={(value) => onChange(value)}
                    required={!assignAllOrganizations}
                    disabled={assignAllOrganizations}
                    useAutocomplete
                    useMultipleSelect
                  />
                  {showBanner && (
                    <Banner type={BannerType.Warning} className="mt-2 text-xs">
                      Any future organizations will not be included when selecting this function. To
                      ensure future organizations are included, select the checkbox under this
                      message titled &apos;Select all organizations&apos;.
                    </Banner>
                  )}
                </div>
              );
            }}
          </FormField>
          <div className="col-span-2">
            <FormField name="status" store={formStore}>
              {({ value, onChange }) => (
                <RadioButtons
                  label="Status"
                  value={value}
                  onChange={onChange}
                  options={[
                    { label: BehaviorConfigStatus.Active, value: BehaviorConfigStatus.Active },
                    { label: BehaviorConfigStatus.Inactive, value: BehaviorConfigStatus.Inactive },
                  ]}
                />
              )}
            </FormField>
          </div>
          <FormField name="assignAllOrganizations" store={formStore}>
            {({ onChange, value }) => (
              <Checkbox
                id="assignAllOrganizations"
                htmlName="assignAllOrganizations"
                label="Select All Organizations"
                onClick={(evt) => onChange(evt.target.checked)}
                value={value}
                wrapperProps={{ className: 'col-span-4' }}
              />
            )}
          </FormField>

          <FormField name="type" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <Listbox
                label="Type"
                data={behaviorConfigTypes}
                selected={value}
                setSelected={(val) => {
                  onChange(val);
                  updateField('isCme', null);
                }}
                required={true}
                error={errorMessage}
              />
            )}
          </FormField>

          {type === BehaviorConfigType.Thumbnail && (
            <FormField name="isCme" store={formStore}>
              {({ value, onChange }) => (
                <RadioButtons
                  label="Is CME?"
                  value={getIsCmeValueToString(value)}
                  onChange={(val) => onChange(getIsCmeFromString(val))}
                  options={behaviorConfigIsCmeOptions}
                />
              )}
            </FormField>
          )}

          <FormField name="diseaseLocationCriteria" store={formStore}>
            {({ value, onChange }) => (
              <Combobox
                id="disease-location-filter"
                htmlName="disease-location-filter"
                label="Disease Location Filter"
                data={locations || []}
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onChange={(value) => onChange(value)}
                useAutocomplete
                useMultipleSelect
                wrapperProps={{ className: 'col-start-3 col-span-2' }}
              />
            )}
          </FormField>

          <FormField name="groupRefCode" store={formStore}>
            {({ value, onChange }) => (
              <TextInput
                id="groupRefCode"
                label="Group Ref Code"
                htmlName="groupRefCode"
                value={value}
                onChange={onChange}
                type="text"
              />
            )}
          </FormField>

          <FormField name="displayPages" store={formStore}>
            {({ value, onChange }) => (
              <MultiListbox
                label="Display Page"
                data={displayPages}
                selected={value}
                setSelected={onChange}
                wrapperProps={{ className: 'col-span-1' }}
                required
              />
            )}
          </FormField>

          <FormField name="diseaseTypeCriteria" store={formStore}>
            {({ value, onChange }) => (
              <Combobox
                id="disease-type-filter"
                htmlName="disease-type-filter"
                label="Disease Type Filter"
                data={pathologies || []}
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onChange={(value) => onChange(value)}
                useAutocomplete
                useMultipleSelect
                wrapperProps={{ className: 'col-start-3 col-span-2' }}
              />
            )}
          </FormField>

          <FormField name="diseaseSettingCriteria" store={formStore}>
            {({ value, onChange }) => (
              <Combobox
                id="disease-setting-filter"
                htmlName="disease-setting-filter"
                label="Disease Setting Filter"
                data={settingData || []}
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onChange={(value) => onChange(value)}
                useAutocomplete
                useMultipleSelect
                wrapperProps={{ className: 'col-start-3 col-span-2' }}
              />
            )}
          </FormField>

          <FormField name="userSpecialties" store={formStore}>
            {({ value, onChange }) => (
              <Combobox
                id="user-specialty"
                htmlName="user-specialty"
                label="User Specialty"
                data={userSpecialties || []}
                value={value}
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onChange={(value) => onChange(value)}
                required
                useAutocomplete
                useMultipleSelect
                wrapperProps={{ className: 'col-span-2' }}
                tooltip={{
                  text: 'If nothing is selected, all options will be included.',
                  icon: <FaInfoCircle className="text-blue-400" />,
                }}
              />
            )}
          </FormField>

          <FormField name="dateRangeFilters" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <div className="grid col-span-2 gap-3 align-bottom grid-cols-15">
                <div className="col-span-7">
                  <DatePicker
                    id="dateRangeStartDate"
                    htmlName="dateRangeStartDate"
                    selected={value && value.startDate}
                    setSelected={(v: Date) => onChange({ ...value, startDate: v })}
                    label="Date Range Filter"
                    error={errorMessage}
                  />
                </div>
                <div className="flex flex-1 col-span-1">
                  <p className="inline-flex items-end mb-2 text-sm font-medium text-gray-700">to</p>
                </div>
                <div className="col-span-7">
                  <DatePicker
                    id="dateRangeEndDate"
                    htmlName="dateRangeEndDate"
                    selected={value && value.endDate}
                    setSelected={(v: Date) => onChange({ ...value, endDate: v })}
                    label=""
                    error={errorMessage}
                  />
                </div>
              </div>
            )}
          </FormField>

          <FormField name="message" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <div className="col-span-3">
                <TextAreaInput
                  id="message"
                  label="Message Markdown"
                  value={value}
                  onChange={onChange}
                  htmlName="message"
                  rows={8}
                  wrapperProps={{ className: '' }}
                  error={errorMessage}
                  required
                />
                <p className="mt-2 text-sm">
                  See{' '}
                  <a
                    href="https://www.markdownguide.org/basic-syntax/"
                    rel="noreferrer"
                    target="_blank"
                    className="font-semibold text-blue-500 underline">
                    basic syntax examples for markdown
                  </a>
                </p>
              </div>
            )}
          </FormField>
          <div className="w-full col-span-2">
            <MessagePreview formStore={formStore} />
          </div>
        </div>
        <div className="flex items-center w-full px-4 pb-4 min-h-fit">
          <Button
            className="w-auto px-6 ml-auto mr-3"
            buttonColor="gray"
            onClick={() => navigate(-1)}>
            Back
          </Button>
          <ButtonWithLoading
            loading={saving}
            className="w-auto px-6"
            buttonColor="blue"
            onClick={onSave}
            disabled={!isValid}>
            Save
          </ButtonWithLoading>
        </div>
      </div>
    </TitleLayout>
  );
};

interface MessagePreviewProps {
  formStore: CreatedFormStore<BehaviorConfigFormValues>;
}

const MessagePreview = ({ formStore }: MessagePreviewProps) => {
  const message = formStore((store) => store.message);
  const type = formStore((store) => store.type);
  const renderPreviewType = () => {
    switch (type) {
      case 'Alert':
        return (
          <Banner>
            <MarkdownContainer>{message}</MarkdownContainer>
          </Banner>
        );
      case 'Thumbnail':
        return (
          <div className="text-sm bg-white border-2 border-orange-400 rounded-md shadow-md h-48 p-2 w-[400px]">
            <MarkdownContainer
              componentOverrides={{
                img: ({ ...props }) => <WrappedImage {...props} />,
              }}>
              {message}
            </MarkdownContainer>
          </div>
        );
      default:
        return null;
    }
  };

  return message && type ? (
    <>
      <label
        htmlFor="message"
        className="inline-flex items-center mb-1.5 text-sm font-medium text-gray-700">
        Preview
      </label>
      {renderPreviewType()}
    </>
  ) : null;
};

const WrappedImage = (props: any) => (
  <div className="inline-flex items-start float-left h-full mr-2">
    <img alt={''} {...props} className="h-auto w-36" />
  </div>
);
